import React, { useState, useCallback } from 'react';
import Sidebar, { useSidebar } from '../../components/Sidebar';
import styles from '../../styles/ReporteAuditoriaStyles/ReporteAuditoria.module.css';
import FiltrosAuditoria from './FiltrosAuditoria';
import TablaAuditoria from './TablaAuditoria';
import { getReporteAuditoria } from '../../api/auditoria';
import { exportReporteAuditoriaXLS } from '../../utils/exportReporteAuditoriaXLS';

const INITIAL_FILTERS = {
  nombre_usuario: '',
  modulo: '',
  pantalla: '',
  fecha_desde: '',
  fecha_hasta: '',
};

function ReporteAuditoriaScreen() {
  const { isCollapsed } = useSidebar();

  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Datos de la página actual
  const [resultados, setResultados] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Todos los resultados (para exportar — se obtienen en una sola llamada sin paginación)
  const [allResultados, setAllResultados] = useState([]);

  // Filtros activos al momento de la última consulta (para que la paginación no cambie filtros)
  const [activeFilters, setActiveFilters] = useState(INITIAL_FILTERS);

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const fetchPage = useCallback(async (currentFilters, pageNum) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getReporteAuditoria({ ...currentFilters, page: pageNum, page_size: 20 });
      setResultados(data.resultados || []);
      setTotal(data.total || 0);
      setPage(data.page || pageNum);
      setTotalPages(data.total_pages || 1);
    } catch (e) {
      setError('Error al obtener la bitácora. Verifica tu conexión o intenta de nuevo.');
      setResultados([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleGenerate = useCallback(async () => {
    setAllResultados([]); // Resetear datos de exportación
    const current = { ...filters };
    setActiveFilters(current);
    await fetchPage(current, 1);
  }, [filters, fetchPage]);

  const handlePageChange = useCallback(async (pageNum) => {
    await fetchPage(activeFilters, pageNum);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeFilters, fetchPage]);

  const handleExport = useCallback(async () => {
    // Si ya tenemos todos los datos cargados, exportamos directo
    if (allResultados.length > 0) {
      exportReporteAuditoriaXLS({ resultados: allResultados, contexto: activeFilters });
      return;
    }
    // Si no, hacemos una sola petición sin paginación (todos los registros)
    setLoading(true);
    try {
      const data = await getReporteAuditoria({ ...activeFilters, no_pagination: true });
      const all = data.resultados || [];
      setAllResultados(all);
      exportReporteAuditoriaXLS({ resultados: all, contexto: activeFilters });
    } catch {
      alert('Error al exportar el reporte. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }, [allResultados, activeFilters]);

  return (
    <div className={styles.mainContainer}>
      <div className={`${styles.layout} ${isCollapsed ? styles.sidebarCollapsedLayout : ''}`}>
        {/* Sidebar */}
        <div className={styles.sidebarColumn}>
          <Sidebar />
        </div>

        {/* Contenido */}
        <div className={styles.contentColumn}>
          <div className={styles.mainContent}>
            <div className={styles.contentArea}>
              <div className={styles.contentWrapper}>
                <FiltrosAuditoria
                  filters={filters}
                  onChange={handleFilterChange}
                  onGenerate={handleGenerate}
                  onExport={handleExport}
                  loading={loading}
                  hasData={resultados.length > 0}
                />

                {error && (
                  <div style={{ color: '#c5221f', background: '#fdecea', padding: '12px 16px', borderRadius: '6px', marginBottom: '16px', fontSize: '14px' }}>
                    {error}
                  </div>
                )}

                <TablaAuditoria
                  resultados={resultados}
                  total={total}
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  loading={loading}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReporteAuditoriaScreen;
